// =============================================================================
// Admin Analytics — MetricCard
// =============================================================================
// A KPI card. Mirrors the Mosaic AnalyticsStatCard layout (same surface,
// border, breakdown bar + legend, and delta treatment) but adds the two-button
// widget toolbar (time-filter + raw-data) that AnalyticsStatCard can't host,
// plus honest badges for caveated metrics and a count-up on numbers.
// =============================================================================
import { ArrowDownRightIcon, ArrowUpRightIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";

import { BreakdownItem, Label, Tooltip } from "@fronterahealth/frontera-ui-components";

import { WidgetBadge, WidgetTimeOverride, WidgetToolbar } from "@pages/AdminAnalytics/components/WidgetChrome";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Count up to a numeric target over ~500ms (respects reduced motion). */
const CountUpValue: React.FC<{ value: number; unit?: string }> = ({ value, unit }) => {
  const [display, setDisplay] = useState(() => (prefersReducedMotion() ? value : 0));
  const fromRef = useRef(0);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDisplay(value);
      return;
    }
    const from = fromRef.current;
    const start = performance.now();
    const duration = 500;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setDisplay(Math.round(from + (value - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  return (
    <>
      {display.toLocaleString()}
      {unit ? <span className="ml-1 text-base font-normal text-text-secondary">{unit}</span> : null}
    </>
  );
};

export interface MetricCardProps {
  title: string;
  infoTooltip?: string;
  /** Numeric values animate (count-up). String values render verbatim (e.g. "4hr 13min"). */
  value: number | string;
  unit?: string;
  /** Already-formatted previous-period value, e.g. "18". Rendered as "… previous period". */
  previousValue?: number | string;
  percentageChange?: number;
  isPositiveChange?: boolean;
  /** Horizontal stacked breakdown bar + legend (e.g. Reports by status). */
  breakdownData?: BreakdownItem[];
  /** When set, each breakdown legend row is clickable (passes the item label). */
  onBreakdownClick?: (label: string) => void;
  /** Tooltip definitions keyed by item label — shown on hover in the legend. */
  definitions?: Record<string, string>;
  loading?: boolean;
  /** Rendered in place of the value when this card's data failed to load. */
  error?: React.ReactNode;
  timeOverride: WidgetTimeOverride;
  onViewData?: () => void;
  badges?: Array<"catalight" | "beta" | "estimated">;
  containerClassName?: string;
  /** Extra content rendered inside the filter popover (e.g. a category picker). */
  filterSlot?: React.ReactNode;
  /** Lights up the dot when a popover filter is non-default (e.g. category changed). */
  extraActive?: boolean;
  /** Resets extra filters (e.g. category) when the reset button is clicked. */
  onResetExtra?: () => void;
  /** Additional icon buttons rendered to the left of the filter button (e.g. a view-mode toggle). */
  extraButtons?: React.ReactNode;
}

const roundedRadius = "9999px";

interface MetricCardBreakdownProps {
  breakdownData: BreakdownItem[];
  total: number;
  definitions?: Record<string, string>;
  onBreakdownClick?: (label: string) => void;
}

const MetricCardBreakdown: React.FC<MetricCardBreakdownProps> = ({
  breakdownData,
  total,
  definitions,
  onBreakdownClick,
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-row items-center justify-between gap-4">
      <div className="flex h-2 w-full max-w-full overflow-hidden rounded-sm bg-gray-100">
        {breakdownData
          .filter((item) => item.value > 0)
          .map((item, index, arr) => {
            const percentage = total === 0 ? 0 : (item.value / total) * 100;
            const isFirst = index === 0;
            const isLast = index === arr.length - 1;
            return (
              <div
                key={item.label}
                className="h-full transition-[width] duration-500 ease-out"
                style={{
                  width: `${percentage}%`,
                  backgroundColor: item.color,
                  marginRight: isLast ? "0" : "2px",
                  borderTopLeftRadius: isFirst ? roundedRadius : "0",
                  borderBottomLeftRadius: isFirst ? roundedRadius : "0",
                  borderTopRightRadius: isLast ? roundedRadius : "0",
                  borderBottomRightRadius: isLast ? roundedRadius : "0",
                }}
              />
            );
          })}
      </div>
      <div className="flex items-center gap-1">
        <Label size="extra-small">Total:</Label>
        <Label size="extra-small" colorType="primary">
          {total}
        </Label>
      </div>
    </div>
    <div className="space-y-2 text-sm">
      {breakdownData.map((item) => {
        const percentage = total === 0 ? 0 : (item.value / total) * 100;
        const clickable = Boolean(onBreakdownClick) && item.value > 0;
        const definition = definitions?.[item.label];
        const Tag = clickable ? "button" : "div";
        return (
          <Tag
            key={item.label}
            type={clickable ? "button" : undefined}
            className={clsx(
              "flex items-center justify-between gap-2 rounded-sm",
              clickable && "-mx-1 cursor-pointer px-1 py-0.5 hover:bg-limestone-50",
            )}
            onClick={clickable ? () => onBreakdownClick!(item.label) : undefined}
            onKeyDown={
              clickable
                ? (e: React.KeyboardEvent) =>
                    (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onBreakdownClick!(item.label))
                : undefined
            }
            title={clickable ? `View ${item.label} reports` : undefined}
          >
            <div className="flex items-center gap-2">
              <span className="inline-block h-1 w-2 rounded-full" style={{ backgroundColor: item.color }} />
              {definition ? (
                <Tooltip text={definition} showInfoIcon={false}>
                  <Label size="extra-small" colorType="primary" className="cursor-default text-gray-700">
                    {item.label}
                  </Label>
                </Tooltip>
              ) : (
                <Label size="extra-small" colorType="primary" className="text-gray-700">
                  {item.label}
                </Label>
              )}
            </div>
            <div className="flex flex-row gap-2 text-gray-600">
              <Label size="extra-small" colorType="primary">
                {item.value}
              </Label>
              <Label size="extra-small" colorType="secondary">
                ({percentage.toFixed(0)}%)
              </Label>
            </div>
          </Tag>
        );
      })}
    </div>
  </div>
);

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  infoTooltip,
  value,
  unit,
  previousValue,
  percentageChange,
  isPositiveChange,
  breakdownData,
  onBreakdownClick,
  loading,
  error,
  timeOverride,
  onViewData,
  badges,
  containerClassName,
  filterSlot,
  extraActive,
  onResetExtra,
  extraButtons,
  definitions,
}) => {
  const hasBreakdown = Boolean(breakdownData?.length);
  const total = breakdownData?.reduce((sum, item) => sum + item.value, 0) ?? 0;

  let cardContent: React.ReactNode;
  if (loading) {
    cardContent = (
      <div className="flex w-full items-center justify-center py-6">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-limestone-600" />
      </div>
    );
  } else if (error) {
    cardContent = error;
  } else if (hasBreakdown) {
    cardContent = (
      <MetricCardBreakdown
        breakdownData={breakdownData!}
        total={total}
        definitions={definitions}
        onBreakdownClick={onBreakdownClick}
      />
    );
  } else {
    let changeColor = "text-text-primary";
    if (typeof percentageChange === "number" && percentageChange !== 0) {
      changeColor = isPositiveChange ? "text-green-500!" : "text-red-500!";
    }
    cardContent = (
      <div className="flex flex-row items-end justify-between">
        <div className="flex flex-col">
          <div className="text-2xl font-bold text-gray-900">
            <Label size="large" colorType="primary">
              {typeof value === "number" ? <CountUpValue value={value} unit={unit} /> : value}
            </Label>
          </div>
          {previousValue !== undefined && (
            <div className="text-sm text-gray-500">
              <Label size="extra-small" className="text-limestone-300">
                {previousValue} previous period
              </Label>
            </div>
          )}
        </div>
        {typeof percentageChange === "number" && (
          <Label size="extra-small" className={clsx("flex items-center text-sm", changeColor)}>
            {Math.abs(percentageChange)}%
            {percentageChange !== 0 && isPositiveChange === true && <ArrowUpRightIcon className="ml-1 h-3 w-3" />}
            {percentageChange !== 0 && isPositiveChange === false && <ArrowDownRightIcon className="ml-1 h-3 w-3" />}
          </Label>
        )}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "flex min-h-[150px] flex-col justify-between gap-4 rounded-lg border border-limestone-200 bg-white p-4 shadow-xs transition-shadow hover:shadow-sm",
        containerClassName,
      )}
    >
      <div className="flex w-full items-start justify-between gap-2">
        <div className="flex min-w-0 grow flex-col gap-1">
          <div className="flex items-center">
            <Label size="small" displayType="loud" colorType="primary" className="whitespace-normal wrap-break-word">
              {title}
            </Label>
            {infoTooltip ? (
              <div className="ml-2 shrink-0">
                <Tooltip infoIconClassName="w-4! h-4! shrink-0" text={infoTooltip} />
              </div>
            ) : null}
          </div>
          {badges?.length ? (
            <div className="flex flex-wrap items-center gap-1">
              {badges.map((b) => (
                <WidgetBadge key={b} kind={b} />
              ))}
            </div>
          ) : null}
        </div>
        <WidgetToolbar
          timeOverride={timeOverride}
          onViewData={onViewData}
          filterSlot={filterSlot}
          extraActive={extraActive}
          onResetExtra={onResetExtra}
          extraButtons={extraButtons}
        />
      </div>
      {cardContent}
    </div>
  );
};
